// hooks/useEditMealAddons.ts
import { useCallback, useState } from 'react';
import { OrderAddonType } from 'models/addon.model';
import { OrderSingleMealEditType } from 'models/meal.model';
import { UpdateOrderBrandMeal } from 'models/order.model';

type Params = {
  setOrderMeals: React.Dispatch<React.SetStateAction<UpdateOrderBrandMeal[]>>;
  brandId?: string;
};

export function useEditMealAddons({ setOrderMeals, brandId }: Params) {
  const [editingMeal, setEditingMeal] = useState<OrderSingleMealEditType | null>(null);

  const syncMealToOrder = useCallback(
    (updatedMeal: OrderSingleMealEditType) => {
      if (!brandId) return;

      setOrderMeals((prev) =>
        prev.map((order) =>
          order.id === brandId
            ? {
                ...order,
                meals: order.meals.map((m) => (m.id === updatedMeal.id ? updatedMeal : m))
              }
            : order
        )
      );
    },
    [brandId, setOrderMeals]
  );

  const incrementAddon = useCallback(
    (addon: OrderAddonType) => {
      if (!editingMeal) return;

      const existing = editingMeal.addons.find((a) => a.id === addon.id);
      if (!existing) return addAddon(addon);

      const updatedMeal = {
        ...editingMeal,
        addons: editingMeal.addons.map((a) =>
          a.id === addon.id ? { ...a, quantity: Number(a.quantity) + 1 } : a
        )
      };

      setEditingMeal(updatedMeal);
      syncMealToOrder(updatedMeal);
    },
    [editingMeal, syncMealToOrder]
  );

  const addAddon = useCallback(
    (addon: OrderAddonType) => {
      if (!editingMeal) return;

      const updatedMeal = {
        ...editingMeal,
        addons: [...editingMeal.addons, { ...addon, quantity: 1, unit_amount: addon.amount }]
      };

      setEditingMeal(updatedMeal);
      syncMealToOrder(updatedMeal);
    },
    [editingMeal, syncMealToOrder]
  );

  const replaceAddon = useCallback(
    (oldAddon: OrderAddonType, newAddon: OrderAddonType) => {
      if (!editingMeal) return;

      const updatedMeal = {
        ...editingMeal,
        addons: [
          ...editingMeal.addons.filter((a) => a.id !== oldAddon.id),
          { ...newAddon, quantity: 1, unit_amount: newAddon.amount }
        ]
      };

      setEditingMeal(updatedMeal);
      syncMealToOrder(updatedMeal);
    },
    [editingMeal, syncMealToOrder]
  );

  const deleteAddon = useCallback(
    (addon: OrderAddonType) => {
      if (!editingMeal) return;

      const updatedMeal = {
        ...editingMeal,
        addons: editingMeal.addons.filter((a) => a.id !== addon.id)
      };

      setEditingMeal(updatedMeal);
      syncMealToOrder(updatedMeal);
    },
    [editingMeal, syncMealToOrder]
  );

  return {
    editingMeal,
    setEditingMeal,
    incrementAddon,
    addAddon,
    replaceAddon,
    deleteAddon
  };
}
